// 各领域接口与 TanStack Query 的 key。页面里直接 useQuery({ queryKey: qk.x, queryFn: () => ep.x() })。
import { site } from "@/lib/site";
import { api, del, get, patch, post, put } from "./client";
import type {
  ActivityItem,
  AdminDemoFilter,
  AdminDemoRowEnriched,
  AdminStats,
  AdminUser,
  AdminUserDetail,
  AdminUserPage,
  AuditFilter,
  AuditLogWithActor,
  AuditFields,
  AuditHistogram,
  AuditTop,
  DemoEnriched,
  DemoSummary,
  QueueStat,
  RiverJobWithDemo,
  UtilityResponse,
  Demo,
  DemoDetail,
  DemoStats,
  DemoStatus,
  Invitation,
  InvitationPreview,
  Items,
  Kill,
  MapsFile,
  Me,
  Member,
  Page,
  PlayerRound,
  Presigned,
  ReplayResponse,
  RiverJob,
  Session,
  SteamAccount,
  SystemStatus,
  Team,
  TeamRole,
  TeamSummary,
  SteamExchange,
  TokenResponse,
  UploadInitResponse,
  User,
} from "./types";

export interface DemoListFilter {
  owner_type?: "user" | "team";
  team_id?: string;
  status?: DemoStatus;
  map?: string;
  source?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}

export const qk = {
  me: ["me"] as const,
  sessions: ["auth", "sessions"] as const,
  maps: ["maps"] as const,
  team: (id: string) => ["teams", id] as const,
  members: (id: string) => ["teams", id, "members"] as const,
  invitations: (id: string) => ["teams", id, "invitations"] as const,
  invitePreview: (code: string) => ["invitations", code] as const,
  demos: (f: DemoListFilter) => ["demos", "list", f] as const,
  /** 无限滚动列表：每页条数不同的列表不能共用缓存。 */
  demosInfinite: (f: DemoListFilter, pageSize: number) => ["demos", "list", f, "infinite", pageSize] as const,
  demosAll: ["demos"] as const,
  demo: (id: string) => ["demos", id] as const,
  demoStats: (id: string) => ["demos", id, "stats"] as const,
  demoKills: (id: string) => ["demos", id, "kills"] as const,
  demoPlayerRounds: (id: string) => ["demos", id, "player-rounds"] as const,
  replay: (id: string) => ["demos", id, "replay"] as const,
  utility: (id: string) => ["demos", id, "utility"] as const,
  admin: {
    status: ["admin", "status"] as const,
    jobs: (f: Record<string, string | undefined>) => ["admin", "jobs", f] as const,
    stats: (days: number) => ["admin", "stats", days] as const,
    queueStats: ["admin", "queues", "stats"] as const,
    users: (f: Record<string, string | undefined>) => ["admin", "users", "list", f] as const,
    user: (id: string) => ["admin", "users", "detail", id] as const,
    activity: (id: string) => ["admin", "users", "activity", id] as const,
    demos: (f: AdminDemoFilter) => ["admin", "demos", "list", f] as const,
    demoSummary: ["admin", "demos", "summary"] as const,
    audit: (f: AuditFilter) => ["admin", "audit", "list", f] as const,
    recentParses: ["admin", "jobs", "recent-parses"] as const,
  },
};

export const ep = {
  // auth
  login: (email: string, password: string) =>
    api<TokenResponse>("/auth/login", { method: "POST", body: { email, password }, anonymous: true }),
  register: (email: string, password: string, display_name: string) =>
    api<TokenResponse>("/auth/register", { method: "POST", body: { email, password, display_name, agreed_version: site.policyVersion }, anonymous: true }),
  steamExchange: (ticket: string) => api<SteamExchange>("/auth/steam/exchange", { method: "POST", body: { ticket }, anonymous: true }),
  steamRegister: (ticket: string, email: string, password: string) =>
    api<TokenResponse>("/auth/steam/register", { method: "POST", body: { ticket, email, password, agreed_version: site.policyVersion }, anonymous: true }),
  logout: () => api<void>("/auth/logout", { method: "POST", body: {}, anonymous: true }),
  logoutAll: () => post<void>("/auth/logout-all"),
  forgotPassword: (email: string) => api<{ message: string }>("/auth/password/forgot", { method: "POST", body: { email }, anonymous: true }),
  resetPassword: (token: string, new_password: string) =>
    api<void>("/auth/password/reset", { method: "POST", body: { token, new_password }, anonymous: true }),
  resendVerification: () => post<{ message: string }>("/auth/email/resend"),
  sessions: () => get<Items<Session>>("/auth/sessions"),

  // me
  me: () => get<Me>("/me"),
  acceptPolicy: () => post<void>("/me/consent", { version: site.policyVersion }),
  updateMe: (body: { display_name?: string }) => patch<User>("/me", body),
  changePassword: (current_password: string, new_password: string) => put<void>("/me/password", { current_password, new_password }),

  // steam
  steamLinkStart: () => post<{ redirect_url: string }>("/steam/link/start"),
  steamRefresh: () => post<SteamAccount>("/steam/refresh"),

  // teams
  createTeam: (name: string, slug?: string) => post<Team>("/teams", { name, slug: slug || undefined }),
  team: (id: string) => get<Team>(`/teams/${id}`),
  updateTeam: (id: string, body: { name?: string; avatar_url?: string }) => patch<Team>(`/teams/${id}`, body),
  deleteTeam: (id: string) => del(`/teams/${id}`),
  members: (id: string) => get<Items<Member>>(`/teams/${id}/members`),
  updateMemberRole: (id: string, userId: string, role: Exclude<TeamRole, "owner">) =>
    patch<void>(`/teams/${id}/members/${userId}`, { role }),
  removeMember: (id: string, userId: string) => del(`/teams/${id}/members/${userId}`),
  transferOwnership: (id: string, userId: string) => post<void>(`/teams/${id}/transfer-ownership`, { user_id: userId }),
  invitations: (id: string) => get<Items<Invitation>>(`/teams/${id}/invitations`),
  createInvitation: (id: string, body: { role?: string; email?: string; expires_in_hours?: number; max_uses?: number }) =>
    post<Invitation>(`/teams/${id}/invitations`, body),
  revokeInvitation: (id: string, invId: string) => del(`/teams/${id}/invitations/${invId}`),
  invitePreview: (code: string) => get<InvitationPreview>(`/invitations/${encodeURIComponent(code)}`),
  acceptInvite: (code: string) => post<TeamSummary>(`/invitations/${encodeURIComponent(code)}/accept`),

  // demos
  demos: (f: DemoListFilter) => get<Page<DemoEnriched>>("/demos", { ...f }),
  demo: (id: string) => get<DemoDetail>(`/demos/${id}`),
  updateDemo: (id: string, body: { title?: string; notes?: string; tags?: string[]; source?: string }) =>
    patch<Demo>(`/demos/${id}`, body),
  deleteDemo: (id: string) => del(`/demos/${id}`),
  reparseDemo: (id: string) => post<Demo>(`/demos/${id}/reparse`),
  uploadInit: (body: { owner: { type: "user" | "team"; team_id?: string }; filename: string; size: number; source?: string; title?: string }) =>
    post<UploadInitResponse>("/demos/upload-init", body),
  uploadComplete: (id: string) => post<Demo>(`/demos/${id}/upload-complete`),
  uploadAbort: (id: string) => post<void>(`/demos/${id}/upload-abort`),
  demoStats: (id: string) => get<DemoStats>(`/demos/${id}/stats`),
  demoKills: (id: string) => get<Items<Kill>>(`/demos/${id}/kills`),
  demoPlayerRounds: (id: string) => get<Items<PlayerRound>>(`/demos/${id}/player-rounds`),
  replay: (id: string) => get<ReplayResponse>(`/demos/${id}/replay`),
  demoDownload: (id: string) => get<Presigned>(`/demos/${id}/download`),
  utility: (id: string) => get<UtilityResponse>(`/demos/${id}/utility`),

  // maps
  maps: () => api<MapsFile>("/maps", { anonymous: true }),

  // admin
  admin: {
    status: () => get<SystemStatus>("/admin/system/status"),
    retryJob: (id: number) => post<RiverJob>(`/admin/jobs/${id}/retry`),
    cancelJob: (id: number) => post<RiverJob>(`/admin/jobs/${id}/cancel`),
    pauseQueue: (name: string) => post<void>(`/admin/queues/${encodeURIComponent(name)}/pause`),
    resumeQueue: (name: string) => post<void>(`/admin/queues/${encodeURIComponent(name)}/resume`),
    updateUser: (id: string, body: { role?: "user" | "admin"; status?: "active" | "disabled"; plan?: string }) =>
      patch<AdminUser>(`/admin/users/${id}`, body),
    reparseDemo: (id: string) => post<Demo>(`/admin/demos/${id}/reparse`),
    reparseOutdated: (body: { replay_version: number; parser_version?: string; limit?: number }) =>
      post<{ candidates: number; enqueued: number }>("/admin/demos/reparse-outdated", body),
    stats: (days = 14) => get<AdminStats>("/admin/stats", { days }),
    queueStats: () => get<{ items: QueueStat[] }>("/admin/queues/stats"),
    jobs: (f: { state?: string; kind?: string; queue?: string; limit?: number; cursor?: string; include?: "demo" }) =>
      get<Page<RiverJobWithDemo>>("/admin/jobs", f),
    users: (f: { q?: string; status?: string; role?: string; cursor?: string; limit?: number }) => get<AdminUserPage>("/admin/users", { limit: 50, ...f }),
    user: (id: string) => get<AdminUserDetail>(`/admin/users/${id}`),
    activity: (id: string, limit = 30) => get<{ items: ActivityItem[] }>(`/admin/users/${id}/activity`, { limit }),
    revokeSessions: (id: string) => post<void>(`/admin/users/${id}/revoke-sessions`),
    resendVerification: (id: string) => post<{ message: string }>(`/admin/users/${id}/resend-verification`),
    demos: (f: AdminDemoFilter) =>
      get<Page<AdminDemoRowEnriched>>("/admin/demos", { limit: 50, ...f, outdated: f.outdated ? "1" : undefined }),
    demoSummary: () => get<DemoSummary>("/admin/demos/summary"),
    audit: (f: AuditFilter) => get<Page<AuditLogWithActor>>("/admin/audit-logs", { limit: 100, ...f }),
    auditHistogram: (f: AuditFilter & { interval?: string }) => get<AuditHistogram>("/admin/audit-logs/histogram", { ...f }),
    auditTop: (f: AuditFilter & { field: string; size?: number }) => get<AuditTop>("/admin/audit-logs/top", { ...f }),
    auditFields: (f: AuditFilter) => get<AuditFields>("/admin/audit-logs/fields", { ...f }),
  },
};

/** Steam 登录入口：顶层跳转（后端写浏览器绑定 cookie 后 302 到 Steam）。 */
export const STEAM_LOGIN_START = "/api/v1/steam/login/start";

/** 地图静态资源 URL（雷达图 / 图标）。 */
export const mapAsset = (map: string, asset: "radar.png" | "radar_lower.png" | "icon.png") =>
  `/api/v1/maps/${encodeURIComponent(map)}/${asset}`;
